import {
  convexQuery,
  useConvexAuth,
  useConvexMutation,
} from "@convex-dev/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { formatCurrency, formatDate } from "@/utils/format";
import { useMutation, useQuery } from "@tanstack/react-query";

import { Button } from "@/components/ui/button";
import { ConvexError } from "convex/values";
import { Id } from "@cvx/_generated/dataModel";
import { Input } from "@/components/ui/input";
import { Loader2 } from "lucide-react";
import { api } from "~/convex/_generated/api";
import { useAuthActions } from "@convex-dev/auth/react";
import { useForm } from "@tanstack/react-form";
import { useState } from "react";
import { useToast } from "@/components/ui/use-toast";
import { z } from "zod";
import { zodValidator } from "@tanstack/zod-form-adapter";

export const Route = createFileRoute("/_app/creator-invites/$inviteId/")({
  component: CreatorInvitePage,
});

function CreatorInvitePage() {
  const { inviteId } = Route.useParams();
  const navigate = useNavigate();
  const [error, setError] = useState<string>();
  const [loginStep, setLoginStep] = useState<
    "signIn" | "signUp" | { email: string; type: "verification" }
  >("signIn");
  const [isSubmittingAuth, setIsSubmittingAuth] = useState(false);
  const { isAuthenticated, isLoading } = useConvexAuth();
  const { signIn } = useAuthActions();
  const { toast } = useToast();

  const { data: user } = useQuery(convexQuery(api.app.getCurrentUser, {}));
  const {
    data: invites,
    isError,
    error: invitesError,
  } = useQuery(convexQuery(api.invites.getCreatorInvitesForUser, {}));

  const invite = invites?.find((invite) => invite._id === inviteId);

  const { mutateAsync: acceptOrRejectInvite, isPending } = useMutation({
    mutationFn: useConvexMutation(api.invites.acceptOrRejectCreatorInvite),
  });

  // Email/password form
  const form = useForm({
    validatorAdapter: zodValidator(),
    defaultValues: {
      email: "",
      password: "",
    },
    onSubmit: async ({ value }) => {
      setIsSubmittingAuth(true);

      try {
        console.log("🚀 Starting authentication with:", {
          step: loginStep,
          email: value.email,
        });

        const result = await signIn("password", {
          ...value,
          flow: loginStep as "signIn" | "signUp",
        });

        console.log("✅ Sign-in result:", {
          result,
          step: loginStep,
          resultType: typeof result,
        });

        // For sign-up, if verification is configured, we need to show verification form
        if (loginStep === "signUp") {
          console.log("📝 Processing sign-up result:", result);
          // If result.signingIn is false, email verification is required
          if (
            result &&
            typeof result === "object" &&
            "signingIn" in result &&
            !result.signingIn
          ) {
            console.log(
              "📧 Verification required - redirecting to verification form",
            );
            setLoginStep({ email: value.email, type: "verification" });
            toast({
              title: "Verification required",
              description: "Please check your email for a verification code.",
            });
            return;
          } else {
            // Sign-up was successful without verification needed
            console.log("🎉 Sign-up successful without verification needed");
            toast({
              title: "Account created!",
              description:
                "Welcome! Your account has been created successfully.",
            });
          }
        } else if (loginStep === "signIn") {
          console.log("🔑 Processing sign-in result:", result);
          // For sign-in, if result.signingIn is false, might need verification
          if (
            result &&
            typeof result === "object" &&
            "signingIn" in result &&
            !result.signingIn
          ) {
            console.log("📧 Sign-in verification required");
            setLoginStep({ email: value.email, type: "verification" });
            toast({
              title: "Verification required",
              description: "Please check your email for a verification code.",
            });
            return;
          } else {
            console.log("🎉 Sign-in successful");
            toast({
              title: "Success",
              description: "Welcome back! You've been signed in.",
            });
          }
        }
      } catch (error) {
        console.error("❌ Authentication error:", error);
        toast({
          variant: "destructive",
          title: "Authentication failed",
          description:
            error instanceof ConvexError
              ? error.data
              : "An unexpected error occurred. Please try again.",
        });
      } finally {
        setIsSubmittingAuth(false);
      }
    },
  });

  // Verification form
  const verificationForm = useForm({
    validatorAdapter: zodValidator(),
    defaultValues: {
      code: "",
    },
    onSubmit: async ({ value }) => {
      if (typeof loginStep !== "object") return;

      setIsSubmittingAuth(true);

      try {
        await signIn("password", {
          email: loginStep.email,
          code: value.code,
          flow: "email-verification",
        });

        toast({
          title: "Email verified!",
          description: "Your account has been successfully verified.",
        });
      } catch (error) {
        toast({
          variant: "destructive",
          title: "Verification failed",
          description:
            error instanceof ConvexError
              ? error.data
              : "Invalid verification code. Please try again.",
        });
      } finally {
        setIsSubmittingAuth(false);
      }
    },
  });

  const handleResendCode = async () => {
    if (typeof loginStep !== "object") return;

    try {
      await signIn("password", { email: loginStep.email, flow: "signUp" });
      toast({
        title: "Code sent",
        description: "A new verification code has been sent to your email.",
      });
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Failed to send code",
        description:
          error instanceof ConvexError
            ? error.data
            : "Unable to send verification code. Please try again.",
      });
    }
  };

  const handleResponse = async (status: "accepted" | "rejected") => {
    try {
      await acceptOrRejectInvite({
        inviteId: inviteId as Id<"creatorInvites">,
        status,
      });

      if (status === "accepted") {
        navigate({ to: "/login" });
      } else {
        setError("Thank you for your response. You can close this window.");
      }
    } catch (err) {
      if (err instanceof ConvexError) {
        setError(err.data);
      } else {
        setError("An unexpected error occurred. Please try again later.");
      }
    }
  };

  if (!isAuthenticated && !isLoading) {
    // Show verification form if in verification step
    if (typeof loginStep === "object" && loginStep.type === "verification") {
      return (
        <div className="flex min-h-screen flex-col items-center justify-center bg-background p-4">
          <div className="w-full max-w-md rounded-lg border border-border bg-card p-6">
            <h1 className="mb-6 text-center text-2xl font-semibold text-primary">
              Check your inbox!
            </h1>
            <p className="mb-8 text-center text-sm text-primary/60">
              We've just emailed you a verification code at{" "}
              <strong>{loginStep.email}</strong>.
              <br />
              Please enter it below to complete your account setup and view your
              partnership invitation.
            </p>

            <form
              className="mb-4 flex w-full flex-col items-start gap-1"
              onSubmit={async (e) => {
                e.preventDefault();
                e.stopPropagation();
                await verificationForm.handleSubmit();
                window.location.reload();
              }}
            >
              <div className="flex w-full flex-col gap-1.5">
                <verificationForm.Field
                  name="code"
                  validators={{
                    onSubmit: z
                      .string()
                      .min(8, "Code must be at least 8 characters."),
                  }}
                  children={(field) => (
                    <Input
                      placeholder="Verification Code"
                      value={field.state.value}
                      onBlur={field.handleBlur}
                      onChange={(e) => field.handleChange(e.target.value)}
                      className={`bg-transparent ${
                        field.state.meta?.errors.length > 0 &&
                        "border-destructive focus-visible:ring-destructive"
                      }`}
                    />
                  )}
                />
                {verificationForm.state.fieldMeta.code?.errors.length > 0 && (
                  <span className="text-sm text-destructive dark:text-destructive-foreground">
                    {verificationForm.state.fieldMeta.code?.errors.join(" ")}
                  </span>
                )}
              </div>

              <Button
                type="submit"
                className="w-full mt-3"
                disabled={isSubmittingAuth}
              >
                {isSubmittingAuth ? (
                  <Loader2 className="animate-spin" />
                ) : (
                  "Verify Email"
                )}
              </Button>
            </form>

            <div className="flex w-full flex-col gap-2">
              <p className="text-center text-sm font-normal text-primary/60">
                Didn't receive the code?
              </p>
              <Button
                onClick={handleResendCode}
                variant="ghost"
                className="w-full hover:bg-transparent"
              >
                Resend Code
              </Button>
              <Button
                onClick={() => setLoginStep("signIn")}
                variant="ghost"
                className="w-full hover:bg-transparent"
              >
                Back to Sign In
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-background p-4">
        <div className="w-full max-w-md rounded-lg border border-border bg-card p-6">
          <h1 className="mb-6 text-center text-2xl font-semibold text-primary">
            Creator Partnership Invitation
          </h1>
          <p className="mb-8 text-center text-sm text-primary/60">
            Please sign in to view and respond to your partnership invitation.
          </p>

          {/* Email/Password Form */}
          <form
            className="mb-4 flex w-full flex-col items-start gap-1"
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              form.handleSubmit();
            }}
          >
            <div className="flex w-full flex-col gap-3">
              <div className="flex w-full flex-col gap-1.5">
                <form.Field
                  name="email"
                  validators={{
                    onSubmit: z
                      .string()
                      .max(256)
                      .email("Email address is not valid."),
                  }}
                  children={(field) => (
                    <Input
                      placeholder="Email"
                      type="email"
                      value={field.state.value}
                      onBlur={field.handleBlur}
                      onChange={(e) => field.handleChange(e.target.value)}
                      className={`bg-transparent ${
                        field.state.meta?.errors.length > 0 &&
                        "border-destructive focus-visible:ring-destructive"
                      }`}
                    />
                  )}
                />
                {form.state.fieldMeta.email?.errors.length > 0 && (
                  <span className="text-sm text-destructive dark:text-destructive-foreground">
                    {form.state.fieldMeta.email?.errors.join(" ")}
                  </span>
                )}
              </div>

              <div className="flex w-full flex-col gap-1.5">
                <form.Field
                  name="password"
                  validators={{
                    onSubmit: z
                      .string()
                      .min(8, "Password must be at least 8 characters."),
                  }}
                  children={(field) => (
                    <Input
                      placeholder="Password"
                      type="password"
                      value={field.state.value}
                      onBlur={field.handleBlur}
                      onChange={(e) => field.handleChange(e.target.value)}
                      className={`bg-transparent ${
                        field.state.meta?.errors.length > 0 &&
                        "border-destructive focus-visible:ring-destructive"
                      }`}
                    />
                  )}
                />
                {form.state.fieldMeta.password?.errors.length > 0 && (
                  <span className="text-sm text-destructive dark:text-destructive-foreground">
                    {form.state.fieldMeta.password?.errors.join(" ")}
                  </span>
                )}
              </div>
            </div>

            <Button
              type="submit"
              className="w-full mt-3"
              disabled={isSubmittingAuth}
            >
              {isSubmittingAuth ? (
                <Loader2 className="animate-spin" />
              ) : loginStep === "signIn" ? (
                "Sign In"
              ) : (
                "Create Account"
              )}
            </Button>
          </form>

          <Button
            variant="ghost"
            className="w-full mb-4"
            onClick={() =>
              setLoginStep(loginStep === "signIn" ? "signUp" : "signIn")
            }
          >
            {loginStep === "signIn"
              ? "Don't have an account? Sign up"
              : "Already have an account? Sign in"}
          </Button>

          <div className="relative flex w-full items-center justify-center mb-4">
            <span className="absolute w-full border-b border-border" />
            <span className="z-10 bg-card px-2 text-xs font-medium uppercase text-primary/60">
              Or continue with
            </span>
          </div>

          <Button
            variant="outline"
            className="w-full gap-2 bg-transparent"
            onClick={async () => {
              try {
                await signIn("google", {
                  redirectTo: window.location.pathname,
                });
              } catch (error) {
                toast({
                  variant: "destructive",
                  title: "Google sign-in failed",
                  description:
                    error instanceof ConvexError
                      ? error.data
                      : "Unable to sign in with Google. Please try again.",
                });
              }
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4"
              viewBox="0 0 24 24"
            >
              <path
                fill="#4285F4"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="#34A853"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="#FBBC05"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="#EA4335"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            Continue with Google
          </Button>
        </div>
      </div>
    );
  }

  if (isLoading || !user) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-background p-4">
        <div className="w-full max-w-md rounded-lg border border-border bg-card p-6 text-center">
          <p className="text-sm text-primary/60">Loading...</p>
        </div>
      </div>
    );
  }

  if (error || isError || !invite) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-background p-4">
        <div className="w-full max-w-md rounded-lg border border-border bg-card p-6 text-center">
          {user && (
            <div className="flex flex-col items-center gap-4">
              <img
                src={user?.image}
                alt={user?.name}
                className="h-16 w-16 rounded-full"
              />
              <div className="flex flex-col items-center gap-1">
                <h3 className="font-medium text-primary">{user?.name}</h3>
                <p className="text-sm text-primary/60">{user?.email}</p>
              </div>
            </div>
          )}

          <p className="mt-6 text-sm text-primary/60">
            {error ||
              invitesError?.message ||
              "Could not find invite, please check your link or contact support"}
          </p>

          {user && (
            <Button
              variant="outline"
              className="mt-6"
              onClick={() => navigate({ to: "/dashboard" })}
            >
              Go back to dashboard
            </Button>
          )}
        </div>
      </div>
    );
  }

  if (invite.status !== "pending") {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-background p-4">
        <div className="w-full max-w-md rounded-lg border border-border bg-card p-6 text-center">
          <h1 className="mb-6 text-center text-2xl font-semibold text-primary">
            Invitation {invite.status === "accepted" ? "Accepted" : "Declined"}
          </h1>
          <p className="mb-8 text-center text-sm text-primary/60">
            You have already{" "}
            {invite.status === "accepted" ? "accepted" : "declined"} this
            partnership invitation.
          </p>
          <Button
            variant="outline"
            onClick={() => navigate({ to: "/dashboard" })}
          >
            Go to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-background p-4">
      <div className="w-full max-w-2xl rounded-lg border border-border bg-card p-8">
        <h1 className="mb-6 text-center text-3xl font-semibold text-primary">
          Partnership Invitation
        </h1>

        <div className="mb-8 space-y-4">
          <div className="text-center">
            <p className="text-lg text-primary">
              <strong>{invite.invitorName}</strong> from{" "}
              <strong>{invite.appName}</strong> has invited you to partner with
              them as a content creator.
            </p>
            {invite.appDescription && (
              <p className="mt-4 text-sm text-primary/60">
                About {invite.appName}: {invite.appDescription}
              </p>
            )}
            {invite.customMessage && (
              <div className="mt-6 rounded-lg bg-secondary p-4">
                <p className="text-sm font-medium text-primary/80">
                  Personal Message:
                </p>
                <p className="mt-2 text-sm text-primary/60 italic">
                  "{invite.customMessage}"
                </p>
              </div>
            )}
          </div>
        </div>

        <div className="mb-8">
          <h2 className="mb-4 text-xl font-semibold text-primary">
            Contract Terms
          </h2>
          <div className="space-y-3 rounded-lg border border-border bg-secondary/50 p-6">
            <div className="flex justify-between">
              <span className="font-medium">Campaign:</span>
              <span>{invite.campaignName}</span>
            </div>

            <div className="flex justify-between">
              <span className="font-medium">Contract Period:</span>
              <span>
                {formatDate(new Date(invite.startDate).toISOString())} -{" "}
                {formatDate(new Date(invite.endDate).toISOString())}
              </span>
            </div>

            {/* Required Videos - New structure with fallback */}
            {(invite.requiredVideos?.count ?? 0) > 0 && (
              <div className="flex justify-between">
                <span className="font-medium">Required Videos:</span>
                <span>
                  {invite.requiredVideos?.count}
                  {invite.requiredVideos?.duration && (
                    <span className="text-sm text-primary/60 ml-1">
                      (
                      {Math.abs(
                        (invite.endDate - invite.startDate) /
                          (1000 * 60 * 60 * 24) -
                          invite.requiredVideos.duration,
                      ) <= 1
                        ? "for the duration of the contract"
                        : `every ${invite.requiredVideos.duration} days`}
                      )
                    </span>
                  )}
                </span>
              </div>
            )}
            {/* Clarification about cross-platform requirements */}
            {(invite.requiredVideos?.count ?? 0) > 0 && (
              <div className="text-sm text-primary/60 italic">
                Note: This is the total number of videos you need to publish
                across all platforms.
              </div>
            )}

            {/* Flat Fee Options - New structure with fallback */}
            {invite.flatFee?.perVideo && (
              <div className="flex justify-between">
                <span className="font-medium">Flat Fee per Video:</span>
                <span>
                  {formatCurrency(
                    invite.flatFee?.perVideo || 0,
                    2,
                    invite.currency,
                  )}
                </span>
              </div>
            )}

            {invite.flatFee?.timePeriod && (
              <div className="flex justify-between">
                <span className="font-medium">
                  {Math.abs(
                    (invite.endDate - invite.startDate) /
                      (1000 * 60 * 60 * 24) -
                      invite.flatFee.timePeriod.days,
                  ) <= 1
                    ? "One Time Payment"
                    : `Flat Fee per ${invite.flatFee.timePeriod.days} days`}
                  :
                </span>
                <span>
                  {formatCurrency(
                    invite.flatFee.timePeriod.amount || 0,
                    2,
                    invite.currency,
                  )}
                </span>
              </div>
            )}

            {/* CPM Rate - New structure with fallback */}
            {invite.cpmRate?.rate && (
              <div className="flex justify-between">
                <span className="font-medium">
                  CPM (Cost Per Thousand Views):
                </span>
                <span>
                  {formatCurrency(
                    invite.cpmRate?.rate || 0,
                    2,
                    invite.currency,
                  )}
                </span>
              </div>
            )}

            {/* Minimum Views - New structure with fallback */}
            {invite.cpmRate?.threshold && (
              <div className="flex justify-between">
                <span className="font-medium">
                  Minimum Views Required
                  {invite.cpmRate?.type === "perVideo" ? " Per Video" : ""}:
                </span>
                <span>{(invite.cpmRate?.threshold || 0).toLocaleString()}</span>
              </div>
            )}

            {/* Explanatory text for total views minimum */}
            {invite.cpmRate?.threshold &&
            invite.cpmRate?.type === "totalViews" ? (
              <div className="text-sm text-primary/60 italic ml-4">
                You must surpass this minimum cumulative view count across all
                videos posted during the contract period to receive CPM payouts.
              </div>
            ) : invite.cpmRate?.threshold &&
              invite.cpmRate?.type === "perVideo" ? (
              <div className="text-sm text-primary/60 italic ml-4">
                You must surpass this minimum view count per video to receive
                CPM payouts.
              </div>
            ) : null}

            {/* Maximum Payout - New structure with fallback */}
            {invite.cpmRate?.maxPayout && (
              <div className="flex justify-between">
                <span className="font-medium">
                  Maximum Payment Cap
                  {invite.cpmRate?.type === "perVideo" ? " Per Video" : ""}:
                </span>
                <span>
                  {formatCurrency(
                    invite.cpmRate?.maxPayout || 0,
                    2,
                    invite.currency,
                  )}
                </span>
              </div>
            )}

            {/* Explanatory text for total views maximum payout */}
            {invite.cpmRate?.maxPayout &&
            invite.cpmRate?.type === "totalViews" ? (
              <div className="text-sm text-primary/60 italic ml-4">
                This is the maximum total amount you can earn during the entire
                contract period.
              </div>
            ) : invite.cpmRate?.maxPayout &&
              invite.cpmRate?.type === "perVideo" ? (
              <div className="text-sm text-primary/60 italic ml-4">
                This is the maximum amount you can earn per video during the
                entire contract period.
              </div>
            ) : null}

            {/* Cutoff Period */}
            {invite.cpmRate?.cutoffPeriod && (
              <>
                <div className="flex justify-between">
                  <span className="font-medium">CPM Calculation Cutoff:</span>
                  <span>{invite.cpmRate.cutoffPeriod} days</span>
                </div>

                {invite.cpmRate.type === "totalViews" ? (
                  <>
                    <div className="text-sm text-primary/60 italic ml-4">
                      Views will be counted {invite.cpmRate.cutoffPeriod} days
                      after the contract ends (
                      {formatDate(new Date(invite.endDate).toISOString())}).
                      Your total cumulative views across all videos will be
                      calculated on{" "}
                      {formatDate(
                        new Date(
                          invite.endDate +
                            invite.cpmRate.cutoffPeriod * 24 * 60 * 60 * 1000,
                        ).toISOString(),
                      )}
                      .
                    </div>
                  </>
                ) : (
                  <div className="text-sm text-primary/60 italic ml-4">
                    For each video, we'll calculate your earnings{" "}
                    {invite.cpmRate.cutoffPeriod} days after you post it. After
                    that date, the earnings for that video are locked in and
                    won't change even if the video gets more views at a later
                    date.
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        <div className="flex justify-center gap-4">
          <Button
            onClick={() => handleResponse("rejected")}
            variant="destructive"
            disabled={isPending}
          >
            Decline
          </Button>
          <Button
            onClick={() => handleResponse("accepted")}
            variant="success"
            disabled={isPending}
          >
            {isPending ? "Processing..." : "Accept Partnership"}
          </Button>
        </div>
      </div>
    </div>
  );
}
